import { IAnnotation, IDataset } from './interfaces';
import _, { isObject, isEmpty, isArray, isFunction, identity } from 'lodash';
import JSON5 from 'json5';
import {
  SUGARCRM_PRIMARY_DATASET_SYSTEM_NAME,
  DATASET_STATUS,
} from '../common';
import { messages } from '../i18n';
import React, { ComponentProps } from 'react';
import { Spinner } from '../common/widgets/Status';
import DatasetError from './DatasetError';
import { IDatasetJson } from '../views/edit-dataset-settings';
import { Column } from 'react-virtualized';
import { HeaderRenderer } from '../components/ui/table';

export function defaultCellDataGetter({ dataKey, rowData }) {
  if (isEmpty(rowData)) {
    return {};
  }

  if (isFunction(rowData.get)) {
    return rowData.get(dataKey);
  } else {
    return rowData[dataKey];
  }
}

const columnProps = [
  'aria-label',
  'cellDataGetter',
  'cellRenderer',
  'className',
  'columnData',
  'dataKey',
  'disableSort',
  'flexGrow',
  'flexShrink',
  'headerClassName',
  'headerRenderer',
  'label',
  'maxWidth',
  'minWidth',
  'style',
  'width',
  'id',
];

export const asTableColumn = ({
  dataKey = 'name',
  width = 200,
  maxWidth = 200,
  headerRenderer = HeaderRenderer,
  style = {},
  headerStyle = {},
  label = messages.table.nameLabel,
  ...remainingColumnProps
}: ComponentProps<typeof Column>) => (
  <Column
    style={{
      height: '100%',
      width,
      maxWidth,
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      ...style,
    }}
    headerStyle={{
      height: '100%',
      width,
      maxWidth,
      flexGrow: 1,
      ...headerStyle,
    }}
    label={label}
    width={width}
    maxWidth={maxWidth}
    key={dataKey}
    dataKey={dataKey}
    headerRenderer={headerRenderer}
    cellDataGetter={defaultCellDataGetter}
    flexGrow={1}
    {..._(remainingColumnProps).pick(columnProps).pickBy(identity).value()}
  />
);

export const DatasetStatusIcon = ({
  statusType,
}: {
  statusType: DATASET_STATUS;
}) => {
  switch (statusType) {
    case DATASET_STATUS.PREPARING:
      return <Spinner>{messages.datasets.preparingYourData}</Spinner>;
      break;
    case DATASET_STATUS.INGESTING:
      return <Spinner>{messages.datasets.refreshingYourData}</Spinner>;
      break;
    case DATASET_STATUS.ERROR:
      return <DatasetError shortDisplay={messages.editDatasetPanel.error} />;
    default:
      return <>{statusType}</>;
  }
};

export const hasSystemNameAnnotation = (
  annotations: IAnnotation[],
): boolean => {
  return _.some(annotations, {
    key: 'systemName',
    value: SUGARCRM_PRIMARY_DATASET_SYSTEM_NAME,
  });
};

export const getPrimarySugarCRMDataset = (
  datasets: IDataset[] = [],
): IDataset => {
  return _.find(datasets, _dataset => {
    const annotations = _.get(_dataset, 'annotations');
    return hasSystemNameAnnotation(annotations);
  });
};

export const parseAndCleanDatasetJSON: (
  json: string | IDatasetJson,
) => IDatasetJson = (json: string | IDatasetJson) => {
  let datasetJson: IDatasetJson = {};

  try {
    datasetJson = isEmpty(json)
      ? {}
      : isObject(json)
        ? json
        : JSON5.parse(json);
  } catch (e) {
    throw new Error(`Malformed DatasetJSON ${e}`);
  }

  if (!isArray(datasetJson.joinedTables)) {
    datasetJson.joinedTables = [];
  }

  return datasetJson;
};
